<script lang="ts">
  import {Timestamp, addDoc, setDoc} from 'firebase/firestore';

  import {
    type ProjectFormData,
    type ProjectDraftFormData,
    type ImageFile,
    ProjectType
  } from '../../types/project';

  import {
    consultingProjectsDb,
    developmentProjectsDb
  } from '../../utils/firebase';
  import uploadFiles from '../../utils/upload-files';
  import isProjectDraft from '../../utils/project-draft';

  import ProjectForm from '../project-form/ProjectForm.svelte';

  export let onSuccess: (createdProjectId: string) => void;
  export let onError: (error: unknown) => void;

  let projectType: ProjectType | null = null;

  $: db =
    projectType === ProjectType.CONSULTING
      ? consultingProjectsDb
      : developmentProjectsDb;

  async function onSubmit(
    projectData: ProjectFormData | ProjectDraftFormData,
    files: Array<ImageFile>,
    captions: Record<string, string>
  ) {
    if (!projectType) {
      console.error('Project type unknown.');
      return;
    }

    const isDraft = isProjectDraft(projectData, projectType);
    const newProject = {
      ...projectData,
      isDraft,
      createdAt: Timestamp.now()
    };

    try {
      const projectRef = await addDoc(db, newProject);

      if (!files || !files.length) {
        onSuccess(projectRef.id);
        return;
      }

      try {
        const imageMedia = await uploadFiles(
          projectType,
          projectRef.id,
          [...files],
          captions
        );
        const videoMedia = projectData.media || [];

        // Add uploaded media to project data
        await setDoc(
          projectRef,
          {media: [...imageMedia, ...videoMedia]},
          {merge: true}
        );
        onSuccess(projectRef.id);
      } catch (error) {
        console.error('Error', error);
        onError(error);
      }
    } catch (error) {
      console.error('Error', error);
      onError(error);
    }
  }
</script>

<style lang="postcss">
  .formContainer {
    align-items: center;
    width: 100%;
    max-width: 67rem;
    margin: 0 auto;
  }

  @media (min-width: 768px) {
    .formContainer {
      padding: 0 3.5rem;
    }
  }

  .typeSelection {
    display: grid;
    grid-template-rows: auto 1fr 1fr;
    gap: 0.5rem;
    height: 20rem;
    max-height: calc(100vh - 4.5rem);
  }

  @media (min-width: 640px) {
    .typeSelection {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: auto 1fr;
      gap: 1rem;
      max-height: calc(100vh - 5.5rem);
    }

    .typeSelection h2 {
      grid-column: 1 /3;
    }
  }

  @media (min-width: 768px) {
    .typeSelection {
      max-height: calc(100vh - 7.5rem);
    }
  }

  h2 {
    margin: 0;
    text-align: center;
  }

  .typeSelection > button {
    height: 100%;
  }

  .development {
    --ubilabs-button-background-color: rgba(var(--ubilabs-green-rgb), 0.1);
    --ubilabs-button-hover-background-color: rgba(
      var(--ubilabs-green-rgb),
      0.3
    );
    --ubilabs-button-active-background-color: rgba(
      var(--ubilabs-green-rgb),
      0.2
    );
    --ubilabs-button-disabled-background-color: rgba(
      var(--ubilabs-green-rgb),
      0.05
    );
    --ubilabs-button-label-color: var(--ubilabs-green-dark-2);
    --ubilabs-button-disabled-label-color: var(--ubilabs-green-light-3);
  }

  .consulting {
    --ubilabs-button-background-color: rgba(var(--ubilabs-yellow-rgb), 0.1);
    --ubilabs-button-hover-background-color: rgba(
      var(--ubilabs-yellow-rgb),
      0.3
    );
    --ubilabs-button-active-background-color: rgba(
      var(--ubilabs-yellow-rgb),
      0.2
    );
    --ubilabs-button-disabled-background-color: rgba(
      var(--ubilabs-yellow-rgb),
      0.05
    );
    --ubilabs-button-label-color: var(--ubilabs-yellow-dark-3);
    --ubilabs-button-disabled-label-color: var(--ubilabs-yellow-light-3);
  }
</style>

<div class="formContainer">
  {#if projectType}
    <ProjectForm {projectType} {onSubmit} />
  {:else}
    <div class="typeSelection">
      <h2>Select the type of your new&nbsp;project</h2>
      <button
        class="development"
        on:click={() => (projectType = ProjectType.DEVELOPMENT)}>
        Development Project
      </button>
      <button
        class="consulting"
        on:click={() => (projectType = ProjectType.CONSULTING)}>
        Consulting Project
      </button>
    </div>
  {/if}
</div>
